import '../TransactionList/index.css'
import '../../styles/card-group.css'
import { Plus, Pencil, Trash2, FastForward, Circle, CheckCircle2, CreditCard } from 'lucide-react'
import { fmtAmt } from '../../lib/format'
import { toDisplayDate } from '../../lib/dateUtils'
import type { Card, Category, Transaction, TransactionListProps } from '../../types'
import { buildRecentTransactionItems } from './grouping'

const CAT_ABBR: Record<string, string> = {
  'Salário': 'SA', 'Mercado': 'ME', 'Aluguel': 'AL', 'Cartão de crédito': 'CC',
  'Terapia': 'TE', 'Lazer': 'LA', 'Transporte': 'TR', 'Saúde': 'SD',
  'Assinatura': 'AS', 'Esportes': 'ES', 'FIES': 'FI', 'Celular': 'CE',
  'Empréstimo': 'EM', 'Seguro': 'SG', 'Beleza': 'BL', 'Viagem': 'VI',
}

const SKELETONS = [1, 2, 3, 4]

interface RecentTransactionsListProps extends TransactionListProps {
  cards: Card[]
  onViewInvoice: (card: Card) => void
  limit?: number
}

export default function RecentTransactionsList({
  items,
  cards,
  subcategories,
  onAdd,
  loading,
  onDelete,
  onEdit,
  onAmortize,
  onDeleteGroup,
  onToggleRealized,
  onViewInvoice,
  limit = 10,
}: RecentTransactionsListProps) {
  const recentItems = buildRecentTransactionItems(items, cards, limit)

  function subName(id: string | null): string | undefined {
    if (!id) return undefined
    return (subcategories as Category[]).find(s => s.id === id)?.name
  }

  function renderTransaction(t: Transaction) {
    return (
      <div key={`tx-${t.id}`} className={'bc-tx' + (t.realized ? '' : ' bc-tx--unrealized')}>
        {onToggleRealized && (
          <button
            className="bc-tx__realize"
            aria-label="Marcar como efetivado"
            onClick={() => onToggleRealized(t.id, t.realized)}
          >
            {t.realized ? <CheckCircle2 size={16} /> : <Circle size={16} />}
          </button>
        )}
        <div className={'bc-tx__icon ' + (t.kind === 'in' ? 'is-in' : 'is-out')}>
          {CAT_ABBR[t.category] ?? t.category.slice(0, 2).toUpperCase()}
        </div>
        <div className="bc-tx__desc">
          <span className="bc-tx__title">{t.title}</span>
          <span className="bc-tx__meta">
            {t.category && <span className="bc-badge bc-badge--cat">{t.category}</span>}
            {subName(t.subcategory_id) && <span className="bc-badge bc-badge--sub">{subName(t.subcategory_id)}</span>}
            {t.note && <span className="bc-tx__note">{t.note}</span>}
          </span>
        </div>
        <span className="bc-tx__date">{t.date}</span>
        <span className={'bc-tx__amt ' + (t.kind === 'in' ? 'is-in' : 'is-out')}>
          {t.kind === 'in' ? '+' : '−'} R$ {fmtAmt(t.amount)}
        </span>
        <div className="bc-tx__actions">
          {onAmortize && t.installment_group_id && (t.installment_index ?? 0) < (t.installment_count ?? 0) && (
            <button
              className="bc-tx__amortize"
              aria-label="Adiantar parcelas"
              onClick={() => onAmortize(t)}
            >
              <FastForward size={14} />
            </button>
          )}
          {onEdit && (
            <button
              className="bc-tx__edit"
              aria-label="Editar lançamento"
              onClick={() => onEdit(t)}
            >
              <Pencil size={14} />
            </button>
          )}
          {onDelete && (
            <button
              className="bc-tx__del"
              aria-label="Excluir lançamento"
              onClick={() => (t.installment_group_id && onDeleteGroup) ? onDeleteGroup(t) : onDelete(t.id)}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="bc-card bc-list">
      <div className="bc-list__header">
        <h3 className="bc-list__title">Lançamentos recentes</h3>
        <button className="bc-btn bc-btn--ghost bc-btn--sm" onClick={onAdd}>
          <Plus size={14} /><span>Adicionar</span>
        </button>
      </div>
      <div className="bc-list__body">
        {loading && SKELETONS.map(n => (
          <div key={n} className="bc-tx">
            <div className="bc-tx__icon bc-tx__skel" style={{ borderRadius: 8 }} />
            <div className="bc-tx__desc">
              <span className="bc-tx__skel" style={{ width: '60%', height: 14, borderRadius: 4 }} />
              <span className="bc-tx__skel" style={{ width: '40%', height: 11, borderRadius: 4, marginTop: 2 }} />
            </div>
            <span className="bc-tx__skel" style={{ width: 28, height: 12, borderRadius: 4 }} />
            <span className="bc-tx__skel" style={{ width: 64, height: 14, borderRadius: 4 }} />
          </div>
        ))}

        {!loading && recentItems.length === 0 && (
          <p className="bc-list__empty">Nenhum lançamento neste mês.</p>
        )}

        {!loading && recentItems.map(item => {
          if (item.type === 'transaction') return renderTransaction(item.transaction)

          return (
            <button
              key={`card-${item.card.id}`}
              className="bc-tx bc-card-group"
              onClick={() => onViewInvoice(item.card)}
            >
              <span />
              <div className="bc-tx__icon bc-card-group__icon">
                <CreditCard size={17} />
              </div>
              <div className="bc-tx__desc">
                <span className="bc-tx__title">{item.card.name}</span>
                <span className="bc-tx__meta">
                  <span className="bc-badge bc-badge--cat">
                    {item.count} {item.count === 1 ? 'lançamento' : 'lançamentos'}
                  </span>
                  <span className="bc-tx__note">Última data {toDisplayDate(item.lastFinancialDate)}</span>
                </span>
              </div>
              <span className="bc-tx__date">{toDisplayDate(item.lastFinancialDate)}</span>
              <span className="bc-tx__amt is-out">− R$ {fmtAmt(item.total)}</span>
              <span className="bc-card-group__open">Ver fatura</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}
